import React from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import { Icon, Tooltip } from "antd";
import get from "lodash/get";
import { connect } from "react-redux";
import moment from "moment-timezone";
import FileLink from "../../components/FileLink/FileLink";

import { logout } from "../../reducers/user/actions";
import {
  getPatientProfileCreatePermission,
  getPatientProfileUpdatePermission,
  getPatientProfileDeletePermission,
  getPatientProfileLockPermission,
  getPatientProfileUnlockPermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import { withParams } from "../../utils/url";
import classes from "./PatientProfileForm.css";
import { withNamespaces } from "react-i18next";

const routes = require("../../routes.json");

const emailRegex = new RegExp(
  /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi
);

class EventMessage extends React.Component {
  renderMessagePart(messagePart) {
    if (emailRegex.test(messagePart)) {
      return (
        <a href={`mailto:${messagePart}`} target="_blank">
          {messagePart}
        </a>
      );
    }

    return messagePart;
  }

  render() {
    return (
      <span>
        {this.props.message.split(emailRegex).map((messagePart, index) => (
          <React.Fragment key={index}>
            {this.renderMessagePart(messagePart)}
          </React.Fragment>
        ))}
      </span>
    );
  }
}

class PatientProfileEventSummary extends React.Component {
  constructor(props) {
    super(props);

    this.containerRef = React.createRef();

    this.state = {
      previewEvents: {},
      showPatientEventSummary: false,
    };
  }

  componentDidMount() {
    moment.tz.setDefault(
      this.props.userTimezone || get(this.props, "site.time_zone") || "UTC"
    );
  }

  componentWillUnmount() {
    moment.tz.setDefault();
  }

  scrollUp = () => {
    window.scrollTo(0, 0);
  };

  togglePreviewEvents = (eventName) => () => {
    this.setState((state) => {
      const eventStatus = state.previewEvents[eventName] ? false : true;
      return {
        previewEvents: { ...state.previewEvents, [eventName]: eventStatus },
      };
    });
  };

  collapseSection = (sectionName) => () => {
    this.setState((state) => ({ [sectionName]: !state[sectionName] }));
  };

  render() {
    const { previewEvents, showPatientEventSummary } = this.state;

    const { state, isNew } = this.props;

    return (
      <div className={classes.patientProfileForm} ref={this.containerRef}>
        {!isNew && (
          <div
            className={classes.patientProfileFormWrapper}
            style={{ marginBottom: 0 }}
          >
            <h4
              className={classnames("headline-default", classes.toggleHeadline)}
              onClick={this.collapseSection("showPatientEventSummary")}
            >
              <Tooltip title={showPatientEventSummary ? "collapse" : "show"}>
                <Icon
                  type="arrow-up"
                  className={classes.collapseIcon}
                  style={{
                    fontSize: "20px",
                    transform: `rotate(${
                      showPatientEventSummary ? "0" : "180deg"
                    })`,
                  }}
                />{" "}
                &nbsp;
              </Tooltip>

              <span className={classes.toggleText}>
                {this.props.i18n.t(
                  "NamespacePatientProfileForm:Patient Event Summary"
                )}
              </span>
            </h4>

            {showPatientEventSummary && (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <td>{this.props.i18n.t("Event")}</td>
                    <td>{this.props.i18n.t("Status")}</td>
                    <td>
                      {this.props.i18n.t(
                        "NamespacePatientProfileForm:Occurred at site"
                      )}
                    </td>
                    <td>
                      {this.props.i18n.t(
                        "NamespacePatientProfileForm:Data entered / Audit Trial"
                      )}
                    </td>
                    <td>{this.props.i18n.t("Actions")}</td>
                  </tr>
                </thead>
                <tbody>
                  {this.props.events_summary_report &&
                    this.props.events_summary_report.map((item, i) => (
                      <tr key={i}>
                        <td>{item.event}</td>
                        <td>
                          {item.status === "green" && (
                            <Icon
                              type="check-square"
                              className={classes.checkIcon}
                            />
                          )}
                          {item.status === "red" && (
                            <Icon
                              type="warning"
                              className={classes.warningIcon}
                            />
                          )}
                          {item.status === "orange" && (
                            <Icon
                              type="clock-circle"
                              className={classes.clockIcon}
                            />
                          )}
                          {item.status === "missing" && (
                            <Icon
                              type="clock-circle"
                              className={classes.clockIconMissing}
                            />
                          )}
                          {item.status === "gray" && (
                            <Icon
                              type="clock-circle"
                              className={classes.checkIconGray}
                            />
                          )}
                          {item.status === "pending" && (
                            <Icon
                              type="clock-circle"
                              className={classes.checkIconGray}
                            />
                          )}
                        </td>
                        <td>{item.site}</td>
                        <td>
                          <div style={{ maxWidth: "500px" }}>
                            <p>
                              <EventMessage message={item.event_comments} />
                            </p>

                            {previewEvents[get(item, "event")] && (
                              <div>
                                {get(item, "other_details.messages", []).map(
                                  (message, index) => (
                                    <p key={index}>{message}</p>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={classes.tableAction}>
                            {["registry_id"].includes(item.event) &&
                              state === "active" && (
                                <Icon
                                  type="edit"
                                  theme="filled"
                                  onClick={this.scrollUp}
                                />
                              )}
                            {["sae"].includes(item.event) &&
                              state === "active" && (
                                <Link
                                  to={withParams(
                                    routes.admin.saeReportingSignle,
                                    { subjectID: this.props.subjectID }
                                  )}
                                >
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["validation"].includes(item.event) &&
                              state === "active" && (
                                <Link
                                  to={withParams(
                                    routes.admin.validateRandomizationAdmin,
                                    {
                                      subjectID: this.props.code,
                                      site: get(this.props, "site.code"),
                                    }
                                  )}
                                >
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["deviation"].includes(item.event) &&
                              state === "active" && (
                                <Link to="/admin/validate-randomization">
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["randomize"].includes(item.event) &&
                              state === "active" && (
                                <Icon
                                  type="edit"
                                  theme="filled"
                                  onClick={this.scrollUp}
                                />
                              )}
                            {get(item, "other_details.document_link") && (
                              <FileLink
                                url={get(item, "other_details.document_link")}
                              />
                            )}
                            {get(item, "other_details.messages") && (
                              <Icon
                                type={
                                  previewEvents[get(item, "event")]
                                    ? "eye-invisible"
                                    : "eye"
                                }
                                theme="filled"
                                onClick={this.togglePreviewEvents(
                                  get(item, "event")
                                )}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  patientProfileCreatePermission: getPatientProfileCreatePermission(state),
  patientProfileUpdatePermission: getPatientProfileUpdatePermission(state),
  patientProfileDeletePermission: getPatientProfileDeletePermission(state),
  patientProfileLockPermission: getPatientProfileLockPermission(state),
  patientProfileUnlockPermission: getPatientProfileUnlockPermission(state),
});

const mapDispatchToProps = { logout };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withNamespaces()(PatientProfileEventSummary));



// WEBPACK FOOTER //
// ./src/components/PatientProfileForm/PatientProfileEventSummary.js